import React, { useState, useContext } from 'react';

import { useNavigate } from 'react-router-dom';

import TenantConfig from '../../config';

/** context */
import { UserContext } from '../../context/UserContext';

/** helpers */
import { sanitizeHtml } from '../../helpers/format';
import { useRudder } from '../../hooks/rudder';

/** service */
import { updateApplication, validateProperty, validateRoutingNumber } from '../../service/api';

const SelectBank = ({ handleContinue, isEdit }) => {
  const { plaidToken, setLoading, appData, updateAppData } = useContext(UserContext);
  const { errorEvent } = useRudder();
  const navigate = useNavigate();

  const [activeIndex, setActiveIndex] = useState(-1);
  const [amazonCheck, setAmazonCheck] = useState(false);
  const [bankAccountCheck, setBankAccountCheck] = useState(false);

  const [accountNumber, setAccountNumber] = useState('');
  const [confirmAccountNumber, setConfirmAccountNumber] = useState('');
  const [accountNumberError, setAccountNumberError] = useState('');
  const [confirmAccountNumberError, setConfirmAccountNumberError] = useState('');

  const [routingNumber, setRoutingNumber] = useState('');
  const [confirmRoutingNumber, setConfirmRoutingNumber] = useState('');
  const [routingNumberError, setRoutingNumberError] = useState('');
  const [confirmRoutingNumberError, setConfirmRoutingNumberError] = useState('');
  const [routingMatchSource, setRoutingMatchSource] = useState(null);

  const [error, setError] = useState('');
  const plaidFeature = appData?.applicationFeatures.find(
    (feature) => feature.dataType === 'plaidtoken'
  );
  const accounts =
    plaidFeature && plaidFeature.value ? plaidFeature.value.financialInstruments : [];

  const handleSubmit = async () => {
    try {
      if (
        TenantConfig?.selectBank.supportVirtual &&
        accounts[activeIndex].meta.isVirtual == 'true' &&
        !accountNumber
      ) {
        setAccountNumberError('Account Number is required.');
        return;
      }

      if (
        TenantConfig?.selectBank.supportVirtual &&
        accounts[activeIndex].meta.isVirtual == 'true' &&
        !routingNumber
      ) {
        setRoutingNumberError('Routing Number is required.');
        return;
      }
      setLoading(true);
      const res = await updateApplication([
        {
          itemType: 'PlaidTokenProperty',
          dataType: 'plaidtoken',
          name: 'plaidToken',
          value: {
            plaidLinkage: {
              plaidNotLinkable: false,
            },
            selectedFinancialInstrumentIndex: activeIndex,
            financialInstruments: TenantConfig?.selectBank.supportVirtual
              ? accounts.map((account, idx) => {
                  if (activeIndex === idx) {
                    return { ...account, accountNumber, routingNumber };
                  }
                  return account;
                })
              : undefined,
          },
        },
      ]);

      if (res && res.data) {
        updateAppData(res.data);
      }
    } catch (e) {
      console.log(e);
      errorEvent('Error API', e);
      setLoading(false);
      if (e?.response?.data?.errors) {
        setError(e?.response?.data?.errors[0].message);
      }
      return false;
    }

    let validationData = await validateProperty('plaidToken,bankAccount');
    setLoading(false);
    if (validationData.data && validationData.data.errors) {
      if (validationData.data.errors.length) {
        validationData.data.errors.map((error) => {
          setError(error.message);
        });
        return false;
      }
    }
    if (isEdit) {
      navigate('/review');
    }
    handleContinue();
  };

  const isSubmitBtnDisabled = () => {
    return (
      activeIndex === -1 ||
      (TenantConfig?.selectBank.renderContinueBtn && (!bankAccountCheck || !amazonCheck))
    );
  };

  const handleValidateRoutingNumber = async (value) => {
    const res = await validateRoutingNumber(value);
    if (res && res.data && res.data?.hits && res.data.hits.hits?.length) {
      const hit = res.data.hits.hits[0];
      setRoutingMatchSource(hit._source);
      setRoutingNumberError('');
      return;
    }
    setRoutingMatchSource(null);
    setRoutingNumberError('No Bank matched this routing number');
  };

  const renderGeneralBankOptions = () => (
    <div className="checkbox-groups" style={{ maxWidth: '500px' }}>
      {accounts &&
        accounts.map((account, index) => (
          <div className="CustomCheckBox left" key={index}>
            <div className="terms-checks">
              <label htmlFor={`custom-checks-${index}`}>
                <input
                  type="checkbox"
                  checked={activeIndex === index}
                  id={`custom-checks-${index}`}
                  name="business-checks"
                  onChange={() => setActiveIndex(index)}
                />
                {account.associatedBankName} #{`<${account.maskedAccountNumber}>`}
              </label>
            </div>
          </div>
        ))}
    </div>
  );

  const renderAmazonBankOptions = () => (
    <>
      <div className="radio-button-groups">
        {accounts &&
          accounts.map((account, index) => (
            <React.Fragment key={index}>
              <div className="custom-radio-button left">
                <div className="terms-checks">
                  <label htmlFor={`custom-radio-${index}`}>
                    <input
                      type="radio"
                      checked={activeIndex === index}
                      id={`custom-radio-${index}`}
                      name="business-checks"
                      onChange={() => setActiveIndex(index)}
                    />
                    {account.associatedBankName} #{`<${account.maskedAccountNumber}>`}
                  </label>
                </div>
              </div>

              {TenantConfig?.selectBank.supportVirtual &&
                activeIndex === index &&
                account.meta?.isVirtual == 'true' &&
                account.meta?.isAccountUpdated == 'false' && (
                  <div className="bank-numbers-section">
                    <label>Please provide the full bank acccount# that you have selected</label>
                    <div className="bank-numbers">
                      <div className="bank-number-field">
                        <label htmlFor="accountNumber">Your Account Number*</label>
                        <input
                          id="accountNumber"
                          placeholder={`xxxxxxxxxxxxx`}
                          value={accountNumber}
                          name="accountNumber"
                          onPaste={(e) => {
                            e.preventDefault();
                            return false;
                          }}
                          onChange={(e) => {
                            setAccountNumber(e.target.value);
                          }}
                          onBlur={() => {
                            if (accountNumber == '') {
                              setAccountNumberError('Account Number is required');
                            } else if (!accountNumber.endsWith(account.maskedAccountNumber)) {
                              setAccountNumberError(
                                'Account Number should end with ' + account.maskedAccountNumber
                              );
                            } else {
                              setAccountNumberError('');
                            }
                          }}
                          spellCheck={false}
                        />
                        {accountNumberError && <p className="error-text">{accountNumberError}</p>}
                      </div>
                      <div className="bank-number-field">
                        <label htmlFor="accountNumberConfirm">Confirm Account Number*</label>
                        <input
                          id="accountNumberConfirm"
                          placeholder={`xxxxxxxxxxxxx`}
                          value={confirmAccountNumber}
                          name="accountNumberConfirm"
                          onPaste={(e) => {
                            e.preventDefault();
                            return false;
                          }}
                          onChange={(e) => {
                            setConfirmAccountNumber(e.target.value);
                          }}
                          onBlur={() => {
                            if (confirmAccountNumber == '') {
                              setConfirmAccountNumberError('Please confirm Account Number');
                            } else if (accountNumber !== confirmAccountNumber) {
                              setConfirmAccountNumberError('Account Number does not match');
                            } else {
                              setConfirmAccountNumberError('');
                            }
                          }}
                          spellCheck={false}
                        />
                        {confirmAccountNumberError && (
                          <p className="error-text">{confirmAccountNumberError}</p>
                        )}
                      </div>
                    </div>
                    <div className="bank-numbers">
                      <div className="bank-number-field">
                        <label htmlFor="routingNumber">Your Routing Number*</label>
                        <input
                          id="routingNumber"
                          placeholder={`xxxxxxxxxxxxx`}
                          value={routingNumber}
                          name="routingNumber"
                          onPaste={(e) => {
                            e.preventDefault();
                            return false;
                          }}
                          onChange={(e) => {
                            setRoutingNumber(e.target.value);
                            if (e.target.value.length == 9) {
                              handleValidateRoutingNumber(e.target.value);
                            }
                          }}
                          onBlur={() => {
                            if (routingNumber == '') {
                              setRoutingMatchSource(null);
                              setRoutingNumberError('Routing Number is required.');
                            } else if (routingNumber.length != 9) {
                              setRoutingMatchSource(null);
                              setRoutingNumberError('Routing # should have 9 digits.');
                            } else {
                              handleValidateRoutingNumber(routingNumber);
                            }
                          }}
                          spellCheck={false}
                        />
                        {routingNumberError && <p className="error-text">{routingNumberError}</p>}
                        {routingMatchSource && (
                          <p className="info">Bank Name: {routingMatchSource['Customer Name']}</p>
                        )}
                      </div>
                      <div className="bank-number-field">
                        <label htmlFor="routingNumberConfirm">Confirm Routing Number*</label>
                        <input
                          id="routingNumberConfirm"
                          placeholder={`xxxxxxxxxxxxx`}
                          value={confirmRoutingNumber}
                          name="routingNumberConfirm"
                          onPaste={(e) => {
                            e.preventDefault();
                            return false;
                          }}
                          onChange={(e) => {
                            setConfirmRoutingNumber(e.target.value);
                          }}
                          onBlur={() => {
                            if (confirmRoutingNumber == '') {
                              setConfirmRoutingNumberError('Please confirm Routing Number.');
                            } else if (routingNumber !== confirmRoutingNumber) {
                              setConfirmRoutingNumberError('Routing Number does not match!');
                            } else {
                              setConfirmRoutingNumberError('');
                            }
                          }}
                          spellCheck={false}
                        />
                        {confirmRoutingNumberError && (
                          <p className="error-text">{confirmRoutingNumberError}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
            </React.Fragment>
          ))}
      </div>
      <div className="checkbox-groups" style={{ maxWidth: '85%' }}>
        <div className="CustomCheckBox left">
          <div className="terms-checks">
            <label htmlFor={`custom-checks`}>
              <input
                type="checkbox"
                id="amazon-seller-checkbox"
                name="business-checks"
                checked={amazonCheck}
                onChange={() => setAmazonCheck(!amazonCheck)}
              />
              By checking here, you confirm that the bank account you selected above is the same as
              in Amazon Seller Central.
            </label>
          </div>
        </div>
        <div className="CustomCheckBox left">
          <div className="terms-checks">
            <label htmlFor={`custom-checks`}>
              <input
                type="checkbox"
                id="bank-account-checkbox"
                name="business-checks"
                checked={bankAccountCheck}
                onChange={() => setBankAccountCheck(!bankAccountCheck)}
              />
              By checking here, you confirm that this is the bank account you want to be funded.
            </label>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="select-bank">
      <h1 className="title">{TenantConfig?.selectBank?.title}</h1>
      <h3
        className="subtitle"
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(TenantConfig?.selectBank?.subTitle) }}
      />
      {TenantConfig?.selectBank.renderAmazonBankOptions
        ? renderAmazonBankOptions()
        : renderGeneralBankOptions()}
      {error && (
        <div className="error-div">
          <p className="error-text">{error}</p>
        </div>
      )}
      <div className="actions">
        <button className="primary-btn" disabled={isSubmitBtnDisabled()} onClick={handleSubmit}>
          Continue
        </button>
      </div>
    </div>
  );
};

export default SelectBank;
